import React, { useState, useEffect } from 'react';
import { TruckData, Delivery } from '../types';
import { trucks, deliveries } from '../data/mockData';

const LiveMap: React.FC = () => {
  const [selectedTruck, setSelectedTruck] = useState<TruckData | null>(null);
  const [currentDelivery, setCurrentDelivery] = useState<Delivery | null>(null);
  
  // Simuler le mouvement des camions
  useEffect(() => {
    const interval = setInterval(() => {
      // Cette fonction simulerait le mouvement des camions sur la carte
      // Dans une vraie application, cela viendrait d'une API
    }, 3000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Trouver la livraison correspondante lorsqu'un camion est sélectionné
  useEffect(() => {
    if (selectedTruck && selectedTruck.currentDeliveryId) {
      const delivery = deliveries.find(d => d.id === selectedTruck.currentDeliveryId);
      setCurrentDelivery(delivery || null);
    } else {
      setCurrentDelivery(null);
    }
  }, [selectedTruck]);

  return (
    <div className="relative w-full h-[600px] bg-gray-100 rounded-lg overflow-hidden shadow-lg">
      {/* Fond de carte simulé */}
      <div className="absolute inset-0 bg-[url('https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/2.3522,48.8566,11,0/1200x600?access_token=pk.dummy')] bg-cover bg-center"></div>
      
      {/* Overlay pour simuler l'interface de carte */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Marqueurs de camions */}
        {trucks.map(truck => (
          <div 
            key={truck.id}
            className={`absolute w-8 h-8 transform -translate-x-1/2 -translate-y-1/2 cursor-pointer pointer-events-auto ${selectedTruck?.id === truck.id ? 'z-20' : 'z-10'}`}
            style={{ 
              left: `${(truck.currentLocation.lng - 2.32) * 1200 + 600}px`, 
              top: `${(48.87 - truck.currentLocation.lat) * 1200 + 300}px` 
            }}
            onClick={() => setSelectedTruck(truck)}
          >
            <div className={`w-full h-full rounded-full flex items-center justify-center ${
              truck.status === 'en_route' ? 'bg-green-500' : 
              truck.status === 'chargement' ? 'bg-blue-500' : 
              truck.status === 'déchargement' ? 'bg-orange-500' : 'bg-gray-500'
            } text-white font-bold shadow-lg transform transition-transform hover:scale-110`}>
              {truck.id}
            </div>
            <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-white rounded-full shadow animate-ping"></div>
          </div>
        ))}
        
        {/* Marqueurs de destinations */}
        {deliveries.map(delivery => (
          <div 
            key={delivery.id}
            className="absolute w-6 h-6 transform -translate-x-1/2 -translate-y-1/2 z-5"
            style={{ 
              left: `${(delivery.destination.lng - 2.32) * 1200 + 600}px`, 
              top: `${(48.87 - delivery.destination.lat) * 1200 + 300}px` 
            }}
          >
            <div className="w-full h-full bg-red-500 rounded-sm transform rotate-45 shadow-md"></div>
          </div>
        ))}
      </div>
      
      {/* Panneau d'information du camion sélectionné */}
      {selectedTruck && (
        <div className="absolute bottom-4 left-4 w-80 bg-white rounded-lg shadow-xl p-4 z-30">
          <div className="flex justify-between items-start">
            <h3 className="font-bold text-lg">Camion #{selectedTruck.id}</h3>
            <button 
              className="text-gray-500 hover:text-gray-700"
              onClick={() => setSelectedTruck(null)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
          
          <div className="mt-2 space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Immatriculation:</span>
              <span className="font-medium">{selectedTruck.licensePlate}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Statut:</span>
              <span className={`font-medium ${
                selectedTruck.status === 'en_route' ? 'text-green-600' : 
                selectedTruck.status === 'chargement' ? 'text-blue-600' : 
                selectedTruck.status === 'déchargement' ? 'text-orange-600' : 'text-gray-600'
              }`}>
                {selectedTruck.status.replace('_', ' ')}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Poids chargé:</span>
              <span className="font-medium">{selectedTruck.loadWeight} kg</span>
            </div>
            
            {currentDelivery && (
              <>
                <div className="border-t border-gray-200 my-2 pt-2">
                  <h4 className="font-semibold">Livraison en cours</h4>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Client:</span>
                  <span className="font-medium">{currentDelivery.clientName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Arrivée estimée:</span>
                  <span className="font-medium">
                    {new Date(currentDelivery.estimatedArrival).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Adresse:</span>
                  <span className="font-medium text-right text-sm">{currentDelivery.destination.address}</span>
                </div>
              </>
            )}
            
            <button className="mt-2 w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-md transition-colors">
              Contacter le chauffeur
            </button>
          </div>
        </div>
      )}
      
      {/* Contrôles de la carte */}
      <div className="absolute top-4 right-4 bg-white rounded-lg shadow-lg p-2 z-30">
        <button className="p-2 hover:bg-gray-100 rounded-lg">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
        </button>
        <button className="p-2 hover:bg-gray-100 rounded-lg">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
        </button>
        <button className="p-2 hover:bg-gray-100 rounded-lg">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
      
      {/* Légende de la carte */}
      <div className="absolute bottom-4 right-4 bg-white bg-opacity-90 rounded-lg shadow-lg p-3 z-30">
        <h4 className="font-semibold text-sm mb-2">Légende</h4>
        <div className="space-y-2 text-sm">
          <div className="flex items-center">
            <div className="w-4 h-4 bg-green-500 rounded-full mr-2"></div>
            <span>En route</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-blue-500 rounded-full mr-2"></div>
            <span>Chargement</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-orange-500 rounded-full mr-2"></div>
            <span>Déchargement</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-red-500 transform rotate-45 mr-2"></div>
            <span>Destination</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveMap;
